/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoiceGuideForm.tsx
 🗓️ Created: 2026-01-19
 🧩 Description:
    Form component for creating voice guides using AI TTS.
    Allows CSV upload/paste for prompt definitions and
    generates audio files in FreeSWITCH sounds directory.

 🔁 Last Updated: 2026-01-19 by Rodrigo Cuadra (FormBox + FixedActionBar)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { Volume2, Upload, FileText, Play, Loader2, Settings, Languages, Mic, CheckCircle, Search, FolderOpen } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { useNotifications } from '../../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../../components/ui/FormBox';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import type { AiProfile } from '../../ai-profiles/api/aiProfilesApi';
import type { VoiceGuide } from '../types';
import { updateVoiceGuideSettings } from '../api/voiceGuidesApi';

import { VoiceGuideCompletionModal } from './VoiceGuideCompletionModal';
import { VoiceGuideFileBrowserModal } from './VoiceGuideFileBrowserModal';

interface VoiceGuideFormProps {
    editingItem?: VoiceGuide | null;
    onClose: () => void;
    onSuccess?: () => void;
    onRefresh?: () => void | Promise<void>; // Refresh list without navigating (used by Save Settings)
}

interface PromptItem {
    path: string;
    filename: string;
    text: string;
    status: 'pending' | 'generating' | 'completed' | 'failed';
}

export default function VoiceGuideForm({ editingItem, onClose, onSuccess, onRefresh }: VoiceGuideFormProps) {
    const { t } = useI18n();
    const { addNotification } = useNotifications();

    // Check if editing existing guide
    const isEditing = !!editingItem;

    // Form state - initialize from editingItem if editing
    const [name, setName] = useState(editingItem?.name || '');
    const [description, setDescription] = useState(editingItem?.description || '');
    const [languageCode, setLanguageCode] = useState(editingItem?.language_code || 'en');
    const [dialectCode, setDialectCode] = useState(editingItem?.dialect_code || 'us');
    const [voiceName, setVoiceName] = useState(editingItem?.voice_name || '');
    const [aiProfileId, setAiProfileId] = useState<string | number | null>(
        editingItem?.ai_profile_id ?? null
    );
    const [startSilenceMs, setStartSilenceMs] = useState(editingItem?.start_silence_ms ?? 50);  // ITU-T P.59 recommended
    const [endSilenceMs, setEndSilenceMs] = useState(editingItem?.end_silence_ms ?? 100);     // ITU-T P.59 recommended
    const [skipExisting, setSkipExisting] = useState(true);  // Skip files that already exist (default: true to avoid throttling)
    const [csvContent, setCsvContent] = useState('');
    const [prompts, setPrompts] = useState<PromptItem[]>([]);
    const [promptSearch, setPromptSearch] = useState('');
    const [showFileBrowser, setShowFileBrowser] = useState(false);

    // UI state
    const [ttsProfiles, setTtsProfiles] = useState<AiProfile[]>([]);
    const [loadingProfiles, setLoadingProfiles] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [progress, setProgress] = useState<{ processed: number; total: number }>({ processed: 0, total: 0 });
    const [completionModal, setCompletionModal] = useState<{
        show: boolean;
        success: boolean;
        generated: number;
        skipped: number;
        failed: number;
        total: number;
        errors: string[];
    }>({ show: false, success: false, generated: 0, skipped: 0, failed: 0, total: 0, errors: [] });

    // Refs
    const abortControllerRef = useRef<AbortController | null>(null);
    const tableScrollRef = useRef<HTMLDivElement | null>(null);

    // Load TTS profiles on mount (same pattern as Recording Management)
    useEffect(() => {
        const loadProfiles = async () => {
            try {
                const { buildApiUrl, getApiHeaders } = await import('../../../config/api');
                const response = await fetch(buildApiUrl('/api/ai/profiles?type=tts&status=true'), {
                    method: 'GET',
                    headers: getApiHeaders(),
                });

                if (response.ok) {
                    const result = await response.json();
                    const profiles = result.data || [];
                    setTtsProfiles(profiles);
                    // Do NOT auto-select: the user must explicitly choose a TTS profile.
                } else {
                    setTtsProfiles([]);
                }
            } catch (err) {
                console.error('Failed to load TTS profiles:', err);
                addNotification({
                    type: 'error',
                    message: t('voiceGuides:form.loadProfilesError') || 'Failed to load TTS profiles'
                });
            } finally {
                setLoadingProfiles(false);
            }
        };
        loadProfiles();
    }, [addNotification, t]);

    // Cleanup: abort generation on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Handle cancel - abort generation if active, otherwise restore original values
    const handleCancel = useCallback(() => {
        if (generating && abortControllerRef.current) {
            // Cancel ongoing generation but stay on form
            abortControllerRef.current.abort();
            addNotification({
                type: 'warning',
                message: t('voiceGuides:form.generationCancelled') || 'Voice guide generation cancelled'
            });
            setGenerating(false);
            setProgress({ processed: 0, total: 0 });
            // Reset prompt statuses to pending
            setPrompts(prev => prev.map(p => ({ ...p, status: 'pending' })));
        } else {
            // Restore original values (edit mode) or reset to defaults (create mode)
            setName(editingItem?.name || '');
            setDescription(editingItem?.description || '');
            setLanguageCode(editingItem?.language_code || 'en');
            setDialectCode(editingItem?.dialect_code || 'us');
            setVoiceName(editingItem?.voice_name || '');
            setAiProfileId(editingItem?.ai_profile_id ?? null);
            setStartSilenceMs(50);  // Reset to default
            setEndSilenceMs(100);   // Reset to default
            setCsvContent('');
            setPrompts([]);
            addNotification({
                type: 'info',
                message: t('voiceGuides:form.formRestored') || 'Form restored to original values'
            });
        }
    }, [generating, editingItem, addNotification, t]);

    // Parse CSV content into prompts
    const parseCsv = useCallback((content: string): PromptItem[] => {
        const lines = content.trim().split('\n').filter(line => line.trim());
        const items: PromptItem[] = [];

        for (const line of lines) {
            const commaIndex = line.indexOf(',');
            if (commaIndex > 0) {
                const pathWithFile = line.substring(0, commaIndex).trim();
                const text = line.substring(commaIndex + 1).trim();

                const lastSlash = pathWithFile.lastIndexOf('/');
                const path = lastSlash > 0 ? pathWithFile.substring(0, lastSlash) : '';
                const filename = lastSlash > 0 ? pathWithFile.substring(lastSlash + 1) : pathWithFile;

                if (filename && text) {
                    items.push({ path, filename, text, status: 'pending' });
                }
            }
        }
        return items;
    }, []);

    // Handle CSV content change
    const handleCsvChange = useCallback((content: string) => {
        setCsvContent(content);
        const parsed = parseCsv(content);
        setPrompts(parsed);
    }, [parseCsv]);

    // Handle file upload
    const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                handleCsvChange(content);
            };
            reader.readAsText(file);
        }
    }, [handleCsvChange]);

    // Handle generate
    const handleGenerate = async () => {
        // Validation: Name (display name)
        const nameValue = name.trim();
        if (!nameValue) {
            addNotification({ type: 'error', message: t('voiceGuides:form.nameRequired') || 'Name is required' });
            return;
        }

        // Validation: Language code (exactly 2 characters)
        if (languageCode.length !== 2) {
            addNotification({ type: 'error', message: t('voiceGuides:form.languageFormat') || 'Language must be exactly 2 characters (e.g., en, es)' });
            return;
        }

        // Validation: Dialect code (exactly 2 characters)
        if (dialectCode.length !== 2) {
            addNotification({ type: 'error', message: t('voiceGuides:form.dialectFormat') || 'Dialect must be exactly 2 characters (e.g., us, mx)' });
            return;
        }

        // Validation: Voice Name (lowercase, no spaces, required)
        const voiceNameValue = voiceName.trim();
        if (!voiceNameValue) {
            addNotification({ type: 'error', message: t('voiceGuides:form.voiceNameRequired') || 'Voice Name is required' });
            return;
        }
        if (!/^[a-z0-9_-]+$/.test(voiceNameValue)) {
            addNotification({ type: 'error', message: t('voiceGuides:form.voiceNameFormat') || 'Voice Name must be lowercase alphanumeric (a-z, 0-9, _, -)' });
            return;
        }

        if (!aiProfileId) {
            addNotification({ type: 'error', message: t('voiceGuides:form.profileRequired') || 'TTS Profile is required' });
            return;
        }
        if (prompts.length === 0) {
            addNotification({ type: 'error', message: t('voiceGuides:form.promptsRequired') || 'At least one prompt is required' });
            return;
        }

        setGenerating(true);

        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();

        const BATCH_SIZE = 10;
        const batches: PromptItem[][] = [];

        // Split prompts into batches
        for (let i = 0; i < prompts.length; i += BATCH_SIZE) {
            batches.push(prompts.slice(i, i + BATCH_SIZE));
        }

        let totalGenerated = 0;
        let totalSkipped = 0;
        let totalFailed = 0;
        // When editing, start with existing voice guide ID to append/overwrite prompts
        let voiceGuideId: number | undefined = isEditing ? editingItem?.id : undefined;
        const errors: string[] = [];

        // Initialize progress
        setProgress({ processed: 0, total: prompts.length });

        try {
            const { generateVoiceGuide } = await import('../api/voiceGuidesApi');

            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                // Check if cancelled
                if (abortControllerRef.current.signal.aborted) {
                    break;
                }

                const batch = batches[batchIndex];
                const startIdx = batchIndex * BATCH_SIZE;

                // Mark current batch as 'generating', preserve already processed items
                setPrompts(prev => prev.map((p, i) => {
                    // Already processed items - keep their status
                    if (i < startIdx) return p;
                    // Current batch - mark as generating
                    if (i >= startIdx && i < startIdx + batch.length) {
                        return { ...p, status: 'generating' };
                    }
                    // Future batches - keep as pending
                    return { ...p, status: 'pending' };
                }));

                // Auto-scroll to show current batch
                if (tableScrollRef.current) {
                    const rowHeight = 36; // Approximate row height in pixels
                    const scrollTo = startIdx * rowHeight;
                    tableScrollRef.current.scrollTo({ top: scrollTo, behavior: 'smooth' });
                }

                try {
                    // For first batch, create the voice guide record
                    // For subsequent batches, only send the prompts (skip_create flag)
                    const response = await generateVoiceGuide({
                        name: name.trim(),
                        description: description.trim() || undefined,
                        language_code: languageCode,
                        dialect_code: dialectCode,
                        voice_name: voiceName.trim(),
                        ai_profile_id: aiProfileId,
                        start_silence_ms: startSilenceMs,
                        end_silence_ms: endSilenceMs,
                        skip_existing: skipExisting,
                        prompts: batch.map(p => ({ path: p.path, filename: p.filename, text: p.text })),
                        voice_guide_id: voiceGuideId, // Pass existing ID for subsequent batches
                    }, abortControllerRef.current.signal);

                    // Save the voice guide ID from first batch
                    if (!voiceGuideId && response.data?.voice_guide_id) {
                        voiceGuideId = response.data.voice_guide_id;
                    }

                    totalGenerated += response.data?.generated_count || 0;
                    totalSkipped += response.data?.skipped_count || 0;
                    totalFailed += response.data?.failed_count || 0;

                    if (response.data?.errors) {
                        errors.push(...response.data.errors);
                    }

                    // Update batch statuses based on response
                    const batchGenerated = response.data?.generated_count || 0;
                    setPrompts(prev => prev.map((p, i) => {
                        if (i < startIdx) return p; // Already processed
                        if (i >= startIdx + batch.length) return p; // Future batches

                        const batchLocalIdx = i - startIdx;
                        return {
                            ...p,
                            status: batchLocalIdx < batchGenerated ? 'completed' : 'failed'
                        };
                    }));

                    // Update progress
                    setProgress(prev => ({ ...prev, processed: startIdx + batch.length }));

                } catch (batchErr) {
                    // Check if aborted
                    if (batchErr instanceof Error && batchErr.name === 'AbortError') {
                        throw batchErr; // Re-throw to handle in outer catch
                    }

                    // Mark batch as failed
                    totalFailed += batch.length;
                    setPrompts(prev => prev.map((p, i) => ({
                        ...p,
                        status: (i >= startIdx && i < startIdx + batch.length) ? 'failed' : p.status
                    })));

                    // Update progress even on error
                    setProgress(prev => ({ ...prev, processed: startIdx + batch.length }));

                    errors.push(`Batch ${batchIndex + 1}: ${batchErr instanceof Error ? batchErr.message : 'Unknown error'}`);
                }
            }

            // Final summary - show completion modal
            if (totalGenerated > 0 || totalSkipped > 0) {
                setCompletionModal({
                    show: true,
                    success: totalFailed === 0,
                    generated: totalGenerated,
                    skipped: totalSkipped,
                    failed: totalFailed,
                    total: prompts.length,
                    errors: errors
                });
            } else {
                addNotification({
                    type: 'error',
                    message: errors[0] || 'All prompts failed to generate'
                });
            }

        } catch (err) {
            // Handle abort
            if (err instanceof Error && err.name === 'AbortError') {
                // Mark remaining as pending (cancelled)
                setPrompts(prev => prev.map(p => ({
                    ...p,
                    status: p.status === 'generating' ? 'pending' : p.status
                })));
                return; // Cancel notification already shown by handleCancel
            }

            // Mark all remaining as failed
            setPrompts(prev => prev.map(p => ({
                ...p,
                status: p.status === 'pending' || p.status === 'generating' ? 'failed' : p.status
            })));
            console.error('Failed to generate voice guide:', err);
            addNotification({
                type: 'error',
                message: err instanceof Error ? err.message : (t('voiceGuides:form.generationError') || 'Failed to generate voice guide')
            });
        } finally {
            setGenerating(false);
            setProgress({ processed: 0, total: 0 });
        }
    };

    // Save settings (TTS Profile + config) without regenerating audio
    const handleSaveSettings = async () => {
        if (!isEditing || !editingItem?.id) return;

        // Capture all values BEFORE any await to avoid stale closures
        const currentProfileId = aiProfileId;
        const currentDescription = description.trim() || null;
        const currentStartSilence = startSilenceMs;
        const currentEndSilence = endSilenceMs;
        const guideId = editingItem.id;

        setSavingSettings(true);
        // DIAGNOSTIC — open browser DevTools (F12) Console to see this
        console.warn('💾 handleSaveSettings called', {
            aiProfileId,
            currentProfileId,
            guideId,
            currentDescription,
        });
        try {
            await updateVoiceGuideSettings(guideId, {
                description: currentDescription,
                // Send Sqids string as-is — the backend uses decodeId() to convert to integer.
                // Do NOT do Number(currentProfileId): Number('qdZW4AXl') = NaN → JSON null!
                ai_profile_id: currentProfileId || null,
                start_silence_ms: currentStartSilence,
                end_silence_ms: currentEndSilence,
            });
            addNotification({
                type: 'success',
                message: t('voiceGuides:form.settingsSaved') || 'Settings saved successfully'
            });
            // Refresh editingItem with fresh data from API
            await onRefresh?.();
        } catch (err) {
            addNotification({
                type: 'error',
                message: err instanceof Error ? err.message : 'Failed to save settings'
            });
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="pb-20">
            {/* Header */}
            <ModuleFormHeader
                icon={<Volume2 className="h-5 w-5 text-primary" />}
                title={isEditing
                    ? (t('voiceGuides:form.editTitle') || 'Edit Voice Guide')
                    : (t('voiceGuides:form.title') || 'Create Voice Guide')
                }
                backButtonText={t('voiceGuides:actions.list') || 'List'}
                onBack={onClose}
                actions={isEditing && editingItem?.id ? [
                    {
                        icon: <FolderOpen className="h-4 w-4" />,
                        tooltip: t('voiceGuides:browser.modalTitle') || 'Browse Audio Files',
                        onClick: () => setShowFileBrowser(true),
                    },
                ] : []}
            />

            <div className="mt-12 space-y-6">
                {/* Configuration FormBox */}
                <FormBox
                    title={t('voiceGuides:form.configuration') || 'Configuration'}
                    icon={<Settings className="h-4 w-4" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Row 1: Name | Input | Description | Input */}
                        <SimpleTooltip
                            content={t('voiceGuides:form.nameTooltip') || 'Single word identifier for the voice guide directory'}
                            title={t('voiceGuides:form.name') || 'Voice Name'}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label htmlFor="name" required className="text-left">
                                {t('voiceGuides:form.name') || 'Voice Name'}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('voiceGuides:form.namePlaceholder') || 'e.g., maria, carlos, custom'}
                        />
                        <SimpleTooltip
                            content={t('voiceGuides:form.descriptionTooltip') || 'Optional description for this voice guide'}
                            title={t('voiceGuides:form.description') || 'Description'}
                            position="top"
                            maxWidth="300px"
                        >
                            <Label htmlFor="description" className="text-left">
                                {t('voiceGuides:form.description') || 'Description'}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('voiceGuides:form.descriptionPlaceholder') || 'Optional description'}
                        />
                    </div>
                </FormBox>

                {/* Language & TTS Profile FormBox */}
                <FormBox
                    title={t('voiceGuides:form.languageSettings') || 'Language & Voice'}
                    icon={<Languages className="h-4 w-4" />}
                >
                    {/*
                      Single 8-column grid — all rows share the same column tracks so
                      every label (col 1 / col 5) and every input (cols 2-4 / cols 6-8) align.

                      Row A:  Language*  [input]  Dialect*  [input]  Voice Name*  [input]  Skip  [●]
                      Row B:  Start Silence  [input──────────ms]  End Silence  [input──────────ms]
                      Row C:  TTS Profile*  [select────────────]  Output Path  [input (read-only)─]
                    */}
                    <div className="grid grid-cols-8 gap-x-3 gap-y-3 items-center">

                        {/* ── Row A ── */}
                        <SimpleTooltip
                            content={t('voiceGuides:form.languageTooltip') || 'ISO 639-1 language code (2 characters, e.g., en, es, fr)'}
                            title={t('voiceGuides:form.language') || 'Language'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="languageCode" required className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.language') || 'Language'}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="languageCode"
                            value={languageCode}
                            onChange={(e) => setLanguageCode(e.target.value.toLowerCase().slice(0, 2))}
                            placeholder="en" maxLength={2}
                            disabled={isEditing}
                            className={isEditing ? 'bg-muted' : ''}
                        />

                        <SimpleTooltip
                            content={t('voiceGuides:form.dialectTooltip') || 'Regional dialect code (2 characters, e.g., us, mx, gb)'}
                            title={t('voiceGuides:form.dialect') || 'Dialect'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="dialectCode" required className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.dialect') || 'Dialect'}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="dialectCode"
                            value={dialectCode}
                            onChange={(e) => setDialectCode(e.target.value.toLowerCase().slice(0, 2))}
                            placeholder="us" maxLength={2}
                            disabled={isEditing}
                            className={isEditing ? 'bg-muted' : ''}
                        />

                        <SimpleTooltip
                            content={t('voiceGuides:form.voiceNameTooltip') || 'Voice identifier used in the sound path (e.g., emma, carlos)'}
                            title={t('voiceGuides:form.voiceName') || 'Voice Name'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="voiceName" required className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.voiceName') || 'Voice Name'}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="voiceName"
                            value={voiceName}
                            onChange={(e) => setVoiceName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                            placeholder="emma"
                            disabled={isEditing}
                            className={isEditing ? 'bg-muted' : ''}
                        />

                        <SimpleTooltip
                            content={t('voiceGuides:form.skipExistingTooltip') || 'Skip generating files that already exist on the server'}
                            title={t('voiceGuides:form.skipExisting') || 'Skip Existing'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="skipExisting" className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.skipExisting') || 'Skip Existing'}
                            </Label>
                        </SimpleTooltip>
                        <Toggle id="skipExisting" value={skipExisting} onChange={setSkipExisting} />

                        {/* ── Row B ── */}
                        <SimpleTooltip
                            content={t('voiceGuides:form.startSilenceTooltip') || 'Silence padding at the beginning of each audio file (ITU-T P.59 recommends 50-100ms)'}
                            title={t('voiceGuides:form.startSilence') || 'Start Silence'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="startSilenceMs" className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.startSilence') || 'Start Silence'}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-3 relative">
                            <Input
                                id="startSilenceMs"
                                type="number" min={0} max={500}
                                value={startSilenceMs}
                                onChange={(e) => setStartSilenceMs(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                                className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">ms</span>
                        </div>

                        <SimpleTooltip
                            content={t('voiceGuides:form.endSilenceTooltip') || 'Silence padding at the end of each audio file (ITU-T P.59 recommends 100-200ms)'}
                            title={t('voiceGuides:form.endSilence') || 'End Silence'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="endSilenceMs" className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.endSilence') || 'End Silence'}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-3 relative">
                            <Input
                                id="endSilenceMs"
                                type="number" min={0} max={500}
                                value={endSilenceMs}
                                onChange={(e) => setEndSilenceMs(Math.min(500, Math.max(0, parseInt(e.target.value) || 0)))}
                                className="pr-10"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">ms</span>
                        </div>

                        {/* ── Row C ── */}
                        <SimpleTooltip
                            content={t('voiceGuides:form.ttsProfileTooltip') || 'AI Profile configured for Text-to-Speech generation'}
                            title={t('voiceGuides:form.ttsProfile') || 'TTS Profile'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="aiProfileId" required className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.ttsProfile') || 'TTS Profile'}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-3">
                            <select
                                id="aiProfileId"
                                value={aiProfileId || ''}
                                onChange={(e) => setAiProfileId(e.target.value || null)}
                                disabled={loadingProfiles}
                                className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                            >
                                <option value="">
                                    {loadingProfiles
                                        ? (t('common:loading') || 'Loading...')
                                        : (t('voiceGuides:form.selectProfile') || 'Select TTS Profile')}
                                </option>
                                {ttsProfiles.map(profile => (
                                    <option key={profile.id} value={profile.id}>
                                        {profile.profile_name} ({profile.provider_name || profile.provider_type})
                                    </option>
                                ))}
                            </select>
                            {ttsProfiles.length === 0 && !loadingProfiles && (
                                <p className="text-xs text-amber-600 mt-1">
                                    {t('voiceGuides:form.noProfiles') || 'No TTS profiles available. Create one in AI Profiles.'}
                                </p>
                            )}
                        </div>

                        <SimpleTooltip
                            content={t('voiceGuides:form.outputPathTooltip') || 'Read-only path where audio files are saved on the server'}
                            title={t('voiceGuides:form.outputPath') || 'Output Path'}
                            position="top" maxWidth="300px"
                        >
                            <Label htmlFor="outputPath" className="text-left whitespace-nowrap">
                                {t('voiceGuides:form.outputPath') || 'Output Path'}
                            </Label>
                        </SimpleTooltip>
                        <div className="col-span-3">
                            <Input
                                id="outputPath"
                                readOnly
                                value={voiceName ? `/sounds/${languageCode}/${dialectCode}/${voiceName}/` : ''}
                                placeholder="/sounds/en/us/emma/"
                                className="w-full bg-muted font-mono text-sm text-primary cursor-default"
                            />
                        </div>
                    </div>
                </FormBox>

                {/* Prompts FormBox */}
                <FormBox
                    title={t('voiceGuides:form.prompts') || 'Prompts'}
                    icon={<Mic className="h-4 w-4" />}
                    headerContent={
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground">
                                {prompts.length} {t('common:items') || 'items'}
                            </span>
                            <label className="cursor-pointer">
                                <input
                                    type="file"
                                    accept=".csv,.txt"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                />
                                <span className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                    <Upload className="h-3 w-3" />
                                    {t('voiceGuides:form.uploadCsv') || 'Upload CSV'}
                                </span>
                            </label>
                        </div>
                    }
                >
                    {prompts.length === 0 ? (
                        <div className="text-center py-8">
                            <Textarea
                                value={csvContent}
                                onChange={(e) => handleCsvChange(e.target.value)}
                                placeholder={`digits/star,Star\ndigits/pound,Pound\ncurrency/dollar,Dollar\ntime/day-0,Sunday`}
                                rows={6}
                                className="font-mono text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                                {t('voiceGuides:form.csvFormat') || 'Format: path/filename,Text to speak (one per line)'}
                            </p>
                        </div>
                    ) : (
                        <div className="border rounded-md overflow-hidden">
                            {/* Search bar inside prompt table */}
                            <div className="bg-muted/20 px-3 py-2 border-b flex items-center gap-2">
                                <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <input
                                    type="text"
                                    value={promptSearch}
                                    onChange={(e) => setPromptSearch(e.target.value)}
                                    placeholder={t('voiceGuides:form.searchPrompts') || 'Search prompts...'}
                                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                />
                                {promptSearch && (
                                    <button
                                        type="button"
                                        onClick={() => setPromptSearch('')}
                                        className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <div ref={tableScrollRef} className="max-h-64 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-card text-xs sticky top-0 z-10 border-b border-border">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-medium w-1/3">{t('voiceGuides:form.file') || 'File'}</th>
                                            <th className="text-left px-3 py-2 font-medium">{t('voiceGuides:form.text') || 'Text'}</th>
                                            <th className="text-center px-3 py-2 font-medium w-20">{t('voiceGuides:form.status') || 'Status'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {prompts
                                            .filter(p =>
                                                !promptSearch ||
                                                `${p.path}/${p.filename}`.toLowerCase().includes(promptSearch.toLowerCase()) ||
                                                p.text.toLowerCase().includes(promptSearch.toLowerCase())
                                            )
                                            .map((prompt, idx) => (
                                            <tr key={idx} className="hover:bg-muted/30">
                                                <td className="px-3 py-2 font-mono text-xs text-primary">
                                                    {prompt.path ? `${prompt.path}/` : ''}{prompt.filename}
                                                </td>
                                                <td className="px-3 py-2 text-xs truncate max-w-[300px]">
                                                    {prompt.text}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {prompt.status === 'pending' && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                                            <FileText className="h-3 w-3" />
                                                        </span>
                                                    )}
                                                    {prompt.status === 'generating' && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-amber-500">
                                                            <Loader2 className="h-3 w-3 animate-spin" />
                                                        </span>
                                                    )}
                                                    {prompt.status === 'completed' && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-green-500">
                                                            ✓
                                                        </span>
                                                    )}
                                                    {prompt.status === 'failed' && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-red-500">
                                                            ✕
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Clear button to re-enter CSV */}
                            <div className="bg-muted/30 px-3 py-2 border-t flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => { setCsvContent(''); setPrompts([]); }}
                                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {t('common:clear') || 'Clear'} & {t('voiceGuides:form.editPrompts') || 'Edit Prompts'}
                                </button>
                            </div>
                        </div>
                    )}
                </FormBox>
            </div >

            {/* Fixed Action Bar */}
            <FixedActionBar
                onCancel={handleCancel}
                cancelText={
                    generating
                        ? (t('common:stop') || 'Stop')
                        : (t('common:cancel') || 'Cancel')
                }
                onSave={handleGenerate}
                saveText={
                    generating
                        ? (t('voiceGuides:form.generating') || 'Generating...')
                        : (t('voiceGuides:form.generate') || 'Generate Voice Guide')}
                saveDisabled={generating || !name || !aiProfileId || prompts.length === 0 || !voiceName}
                isSaving={generating}
            >
                {/* Save Settings button (edit mode only) */}
                {isEditing && (
                    <button
                        type="button"
                        onClick={handleSaveSettings}
                        disabled={savingSettings || generating || !aiProfileId}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-primary text-primary bg-background hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {savingSettings ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <CheckCircle className="h-4 w-4" />
                        )}
                        {savingSettings
                            ? (t('common:saving') || 'Saving...')
                            : (t('voiceGuides:form.saveSettings') || 'Save Settings')}
                    </button>
                )}
                {/* Progress Bar during generation */}
                {
                    generating && progress.total > 0 && (
                        <>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-primary transition-all duration-300 ease-out"
                                    style={{ width: `${Math.round((progress.processed / progress.total) * 100)}%` }}
                                />
                            </div>
                            <span className="text-sm text-muted-foreground whitespace-nowrap ml-3">
                                {progress.processed} / {progress.total} ({Math.round((progress.processed / progress.total) * 100)}%)
                            </span>
                        </>
                    )
                }
            </FixedActionBar >

            {/* Completion Modal */}
            <VoiceGuideCompletionModal
                completionModal={completionModal}
                onGenerateMore={() => {
                    setCompletionModal({ ...completionModal, show: false });
                    setPrompts([]);
                    setCsvContent('');
                }}
                onClose={() => {
                    setCompletionModal({ ...completionModal, show: false });
                    onSuccess?.();
                    onClose();
                }}
                t={t}
            />

            {/* Audio File Browser Modal — opens from header icon (edit mode only) */}
            {isEditing && editingItem?.id && (
                <VoiceGuideFileBrowserModal
                    isOpen={showFileBrowser}
                    onClose={() => setShowFileBrowser(false)}
                    guideId={editingItem.id}
                    soundPath={editingItem.sound_path || `${languageCode}/${dialectCode}/${voiceName}/`}
                    guideName={editingItem.name}
                />
            )}
        </div >
    );
}
